import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  getAllDocuments,
  getDocument,
  getSharedDocument,
  createDocument,
  updateDocument,
  deleteDocument,
  signDocument,
  publishDocument,
  downloadDocumentAsPDF,
  unpublishDocument,
} from "../functions/api_fethcers";
import { toast } from "react-toastify";

export const useDocument = (
  id,
  token,
  // fetchAll: false lets a caller that only needs the mutations (e.g. just
  // publish/unpublish) opt out of fetching the entire global documents list.
  options = { page: 0, limit: 10, filters: {}, exclude_types: [], fetchAll: true },
  // Advisory recipient token from an emailed share link (?r=). Forwarded to the API so
  // the first-open notification can name the client instead of "Anonymous Viewer".
  recipientToken = null
) => {
  const queryClient = useQueryClient();

  // Ensure we don't fetch both types simultaneously
  const isSharedDoc = Boolean(token);
  const documentId = isSharedDoc ? null : id;

  // Document fetch query
  const documentQuery = useQuery(
    ["document", id, token, recipientToken],
    async () => {
      if (token) {
        // For shared documents
        const response = await getSharedDocument(token, recipientToken);
        return {
          ...response.document, // Spread the document metadata
          content: response.html_content, // Store HTML content in content field
          isShared: true, // Flag to indicate this is a shared document
        };
      }
      if (id) {
        const result = await getDocument(id);
        return result;
      }
      return null;
    },
    {
      enabled: Boolean(id) || Boolean(token),
      staleTime: 0,
      // Ensure refetch on id change
      refetchOnMount: true,
      retry: 1,
      onError: (error) => {
        toast.error(error.message || "Failed to fetch document");
      },
    }
  );

  // All documents query (only for regular document view)
  // Serialize options to a stable JSON string for the query key so React Query detects changes
  const allDocumentsQuery = useQuery(
    ["documents", JSON.stringify(options)],
    () => getAllDocuments(options),
    {
      enabled: !isSharedDoc && options.fetchAll !== false,
      keepPreviousData: true,
      staleTime: 0, // Always refetch when key changes
    }
  );

  // Create document mutation
  const createDocumentMutation = useMutation(createDocument, {
    onSuccess: (newDocument) => {
      // Immediately set the query data for the new document
      queryClient.setQueryData(["document", newDocument._id], newDocument);

      // Also update the documents list
      queryClient.setQueryData("documents", (old) => {
        return old ? [...old, newDocument] : [newDocument];
      });
      return newDocument; // Make sure to return the new document
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create document");
    },
  });

  // Update document mutation
  const updateDocumentMutation = useMutation(updateDocument, {
    onSuccess: () => {
      if (documentId) {
        queryClient.invalidateQueries(["document", documentId]);
      }
      queryClient.invalidateQueries("documents");
      // toast.success('Document updated successfully!');
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update document");
    },
  });

  // Delete document mutation
  const deleteDocumentMutation = useMutation(deleteDocument, {
    onMutate: async (deletedDocId, deletedDocType) => {
      await queryClient.cancelQueries("documents");

      // Snapshot the previous value
      const previousDocuments = queryClient.getQueryData("documents");

      // Optimistically update to remove the document
      queryClient.setQueryData("documents", (old) =>
        old ? old.filter((doc) => doc._id !== deletedDocId) : []
      );
      return { previousDocuments };
    },
    onSuccess: () => {
      queryClient.invalidateQueries("documents");
      toast.success("Document deleted successfully!");
    },
    onError: (error, deletedDocId, context) => {
      // Rollback to the previous state if the mutation fails
      queryClient.setQueryData("documents", context.previousDocuments);
      toast.error(error.message || "Failed to delete document");
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries("documents");
    },
  });

  // Download document mutation
  const downloadDocumentMutation = useMutation(
    // Accept optional id parameter in mutation function
    (mutationId = null) => {
      // Use passed-in ID or fall back to documentId from hook
      const idToUse = mutationId || documentId;

      if (!idToUse) {
        throw new Error("Cannot download document without ID");
      }
      return downloadDocumentAsPDF(idToUse);
    },
    {
      onError: (error) => {
        toast.error(error.message || "Failed to download document");
      },
      onSuccess: () => {
        toast.success("Document downloaded successfully!");
      },
      retry: 0,
    }
  );

  // Publish document mutation
  const publishDocumentMutation = useMutation(
    (publishId = null) => {
      const idToPublish = publishId || documentId;
      if (!idToPublish) {
        throw new Error("Cannot publish document without ID");
      }
      return publishDocument(idToPublish);
    },
    {
      onMutate: async (publishId = null) => {
        const idToPublish = publishId || documentId;
        if (!idToPublish) return;

        await queryClient.cancelQueries(["document", idToPublish]);

        const previousDocument = queryClient.getQueryData([
          "document",
          idToPublish,
        ]);

        queryClient.setQueryData(["document", idToPublish], (old) => ({
          ...old,
          shareable_link: "generating...",
          // link_expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days from now
        }));

        return { previousDocument };
      },
      onError: (error, publishId, context) => {
        const idToPublish = publishId || context?.idToPublish;
        if (idToPublish) {
          queryClient.setQueryData(
            ["document", idToPublish],
            context?.previousDocument
          );
        }
        toast.error(error.message || "Failed to publish document");
      },
      onSuccess: (data, publishId, context) => {
        const idToPublish = publishId || context?.idToPublish;
        if (idToPublish) {
          queryClient.setQueryData(["document", idToPublish], (old) => ({
            ...old,
            ...data,
          }));
        }
        queryClient.invalidateQueries(["document", idToPublish]);
        queryClient.invalidateQueries(["documents", options]);
        // Invalidate invoices to trigger refetch in useInvoices
        queryClient.invalidateQueries("invoices");

        toast.success("Document published successfully!");
      },
    }
  );

  // Unpublish document mutation
  const unpublishDocumentMutation = useMutation(
    (unpublishId = null) => {
      const finalId = unpublishId || documentId;
      if (!finalId) {
        throw new Error("Cannot unpublish document without ID");
      }
      return unpublishDocument(finalId);
    },
    {
      onMutate: async (unpublishId = null) => {
        const finalId = unpublishId || documentId;
        if (!finalId) return;

        await queryClient.cancelQueries(["document", finalId]);

        const previousDocument = queryClient.getQueryData([
          "document",
          finalId,
        ]);

        queryClient.setQueryData(["document", finalId], (old) => ({
          ...old,
          shareable_link: null,
          first_opened_at: null,
          last_opened_at: null,
          open_count: null,
        }));

        return { previousDocument };
      },
      onError: (error, unpublishId, context) => {
        const finalId = unpublishId || context?.finalId;
        if (finalId) {
          queryClient.setQueryData(
            ["document", finalId],
            context?.previousDocument
          );
        }
        toast.error(error.message || "Failed to unpublish document");
      },
      onSuccess: (data, unpublishId, context) => {
        const finalId = unpublishId || context?.finalId;
        if (finalId) {
          queryClient.setQueryData(["document", finalId], data);
        }
        queryClient.invalidateQueries(["document", finalId]);
        queryClient.invalidateQueries(["documents", options]);
        // Invalidate invoices to trigger refetch in useInvoices
        queryClient.invalidateQueries("invoices");
        toast.success("Document unpublished successfully!");
      },
    }
  );

  // Sign document mutation
  const signDocumentMutation = useMutation(
    // Use stored token from hook context
    (signatureData) => signDocument(token, signatureData),
    {
      onMutate: async (signatureData) => {
        if (!token) return;

        await queryClient.cancelQueries(["document", null, token]);

        const previousDocument = queryClient.getQueryData([
          "document",
          null,
          token,
        ]);

        // Update the cached document with optimistic update
        queryClient.setQueryData(["document", null, token], (old) => ({
          ...old,
          signed_by: signatureData.signer_name,
          signature_data: signatureData.signature_img,
          signed_at: signatureData.signature_date,
        }));

        return { previousDocument };
      },
      onError: (error, _variables, context) => {
        if (token) {
          queryClient.setQueryData(
            ["document", null, token],
            context?.previousDocument
          );
        }

        const errorMessage = error.message || "Failed to sign document";
        if (errorMessage.includes("already signed")) {
          toast.error("This document has already been signed");
        } else if (errorMessage.includes("expired")) {
          toast.error("This signing link has expired");
        } else {
          toast.error("Failed to sign document. Please try again.");
        }
      },
      onSuccess: (data) => {
        if (token) {
          // Update the cached document with the server response
          queryClient.setQueryData(["document", null, token], (old) => ({
            ...old,
            ...data,
            content: data.html_content, // Ensure HTML content is properly updated
          }));
          toast.success("Document signed successfully!");
        }
      },
    }
  );

  return {
    // Document data
    document: documentQuery.data,
    isLoading: documentQuery.isLoading,
    isError: documentQuery.isError,
    error: documentQuery.error,

    // All documents (with pagination)
    allDocuments: allDocumentsQuery.data?.items ?? [],
    pagination: {
      currentPage: allDocumentsQuery.data?.page ?? 0,
      totalPages: allDocumentsQuery.data?.total_pages ?? 0,
      totalItems: allDocumentsQuery.data?.total ?? 0,
      hasMore: allDocumentsQuery.data?.has_more ?? false,
    },
    isLoadingAll: allDocumentsQuery.isLoading,
    isErrorAll: allDocumentsQuery.isError,

    // Mutations
    createDocument: createDocumentMutation.mutateAsync,
    updateDocument: updateDocumentMutation.mutateAsync,
    deleteDocument: deleteDocumentMutation.mutate,
    publishDocument: publishDocumentMutation.mutateAsync,
    signDocument: signDocumentMutation.mutateAsync,
    downloadDocument: downloadDocumentMutation.mutateAsync,
    unpublishDocument: unpublishDocumentMutation.mutateAsync,

    // Publishing states
    isPublishing: publishDocumentMutation.isLoading,
    publishError: publishDocumentMutation.error,
    isUnpublishing: unpublishDocumentMutation.isLoading,
    unpublishError: unpublishDocumentMutation.error,

    // Signing states
    isSigningDocument: signDocumentMutation.isLoading,
    signError: signDocumentMutation.error,

    // Download states
    isDownloading: downloadDocumentMutation.isLoading,
    downloadError: downloadDocumentMutation.error,

    // Helper flags
    isSharedDocument: isSharedDoc,
  };
};
